import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { requestForToken } from "../firebase";
import "../css/Login.css";

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  name,
  error,
  loading,
  disabled,
  required,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const selected = options.find((opt) => opt.value == value);
    setSearchTerm(selected ? selected.label : "");
  }, [value, options]);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (onChange) {
      onChange({ target: { name, value: "" } });
    }
    setIsOpen(true);
  };

  const handleSelect = (selectedValue) => {
    setSearchTerm("");
    setIsOpen(false);
    if (onChange) {
      onChange({ target: { name, value: selectedValue } });
    }
  };

  const displayValue = options.find((opt) => opt.value == value)?.label || "";

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <div className={`input-group ${error ? "error" : ""}`}>
        <div className="input-icon">
          <i className="fas fa-search"></i>
        </div>
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={displayValue || searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          disabled={disabled || loading}
          readOnly={!isOpen}
          required={required}
        />
        {isOpen && (
          <ul
            className="dropdown-list"
            style={{ width: "70%", textAlign: "left" }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className="dropdown-item"
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className="dropdown-item no-results">No results found</li>
            )}
          </ul>
        )}
      </div>
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}
      {loading && <div className="loading-message">Loading...</div>}
    </div>
  );
};

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const server_folder = localStorage.getItem("server_folder");

  const server_url =
    localStorage.getItem("server_url") ||
    "https://mydigigarage.com/v1/";
  const company_id = 2;

  localStorage.getItem("garageLoginNumber") && navigate("/login");

  const [mode, setMode] = useState("signup");
  const [formData, setFormData] = useState({
    type: "1",
    grage_name: "",
    grage_contact_person_name: "",
    grage_number: "",
    grage_email: "",
    grage_address: "",
    grage_city_id: "",
    grage_state_id: "",
    grage_gst_no: "",
    grage_referral_code: "", // New field for user input
    otp: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateLoading, setStateLoading] = useState(true);
  const [cityLoading, setCityLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");
  const [timer, setTimer] = useState(0);
  const [mobileVerified, setMobileVerified] = useState("");

  // Get referral code from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get("ref");
    if (refCode) {
      setFormData((prev) => ({
        ...prev,
        grage_referral_code: refCode,
      }));
    }
  }, [location]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (apiError || apiSuccess) {
      const timer = setTimeout(() => {
        setApiError("");
        setApiSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [apiError, apiSuccess]);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      if (!server_url) {
        setApiError("Server URL not configured.");
        return;
      }
      setStateLoading(true);
      try {
        const formDataToSend = new URLSearchParams();
        formDataToSend.append("company_id", company_id || "2");

        const response = await axios.post(
          `${server_url}state.php`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.status == "1") {
          const stateOptions = (response.data.result || []).map((state) => ({
            value: state.state_id,
            label: state.state_name,
          }));
          setStates(stateOptions);
        } else {
          setApiError(response.data.error || "Failed to fetch states");
        }
      } catch (error) {
        console.error("Fetch states error:", error);
        setApiError("Unable to fetch states. Please try again.");
      } finally {
        setStateLoading(false);
      }
    };

    fetchStates();
  }, [server_url, company_id]);

  // Fetch cities when state_id changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.grage_state_id || !company_id) {
        setCities([]);
        return;
      }
      setCityLoading(true);
      try {
        const formDataToSend = new URLSearchParams();
        formDataToSend.append("company_id", company_id);
        formDataToSend.append("state_id", formData.grage_state_id);

        const response = await axios.post(
          `${server_url}city.php`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.status == "1") {
          const cityOptions = (response.data.result || []).map((city) => ({
            value: city.city_id,
            label: city.city_name,
          }));
          setCities(cityOptions);
        } else {
          setApiError(response.data.error || "Failed to fetch cities");
          setCities([]);
        }
      } catch (error) {
        console.error("Fetch cities error:", error);
        setApiError("Unable to fetch cities. Please try again.");
        setCities([]);
      } finally {
        setCityLoading(false);
      }
    };

    fetchCities();
  }, [formData.grage_state_id, server_url, company_id]);

  const validateSignupForm = () => {
    const newErrors = {};

    if (!formData.grage_name.trim()) {
      newErrors.grage_name = "Garage name is required";
    }

    if (!formData.grage_number.trim()) {
      newErrors.grage_number = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.grage_number)) {
      newErrors.grage_number = "Please enter a valid 10-digit mobile number";
    }

    if (!formData.grage_state_id) {
      newErrors.grage_state_id = "State is required";
    }

    if (!formData.grage_city_id) {
      newErrors.grage_city_id = "City is required";
    }

    // Validate referral code format if provided
    if (
      formData.grage_referral_code.trim() &&
      !/^[A-Z0-9]{8}$/.test(formData.grage_referral_code.toUpperCase())
    ) {
      newErrors.grage_referral_code =
        "Referral code must be 8 alphanumeric characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length == 0;
  };

  const validateVerifyForm = () => {
    const newErrors = {};

    if (!formData.otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = "Please enter a valid 6-digit OTP";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length == 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name == "grage_gst_no") {
      processedValue = value.toUpperCase();
    } else if (name == "grage_referral_code") {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    if (apiError) {
      setApiError("");
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    if (!validateSignupForm()) {
      return;
    }

    setLoading(true);
    setApiError("");
    setApiSuccess("");

    try {
      const payload = {
        ...formData,
        type: "1",
        grage_referral_code: formData.grage_referral_code.trim() || "", // Send empty string if no referral
      };
      delete payload.otp;

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        formDataToSend.append(key, payload[key]);
      }

      const response = await axios.post(
        `${server_url}signup.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status == "0") {
        setApiError(response.data.error);
        setLoading(false);
      } else if (response.data.status == "1") {
        toast.success("OTP sent to your mobile number");
        setMode("verify");
        setMobileVerified(formData.grage_number);
        setTimer(60);
        setLoading(false);
      } else {
        setApiError(response.data.error || "Signup failed");
        setLoading(false);
      }
    } catch (error) {
      console.error("Signup error:", error);
      if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      } else if (error.response?.status == 404) {
        setApiError("Signup endpoint not found. Check server URL.");
      } else {
        setApiError("Unable to connect to server. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;

    setLoading(true);

    try {
      const payload = {
        type: "1",
        grage_name: formData.grage_name,
        grage_contact_person_name: formData.grage_contact_person_name,
        grage_number: mobileVerified,
        grage_email: formData.grage_email,
        grage_address: formData.grage_address,
        grage_city_id: formData.grage_city_id,
        grage_state_id: formData.grage_state_id,
        grage_gst_no: formData.grage_gst_no,
        grage_referral_code: formData.grage_referral_code || "",
      };

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        formDataToSend.append(key, payload[key]);
      }

      const response = await axios.post(
        `${server_url}signup.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status == "1") {
        toast.success("OTP resent successfully!");
        setTimer(60);
      } else {
        setApiError(response.data.error || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setApiError("Unable to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Request notification permission for FCM
  const requestNotificationPermission = async () => {
    try {
      if (!("Notification" in window)) {
        return false;
      }

      // Check current permission
      let permission = Notification.permission;

      // If permission is already granted, return true
      if (permission === "granted") {
        return true;
      }

      // If permission is denied, don't ask again
      if (permission === "denied") {
        return false;
      }

      // Request permission with a timeout to prevent hanging
      const permissionPromise = Notification.requestPermission();
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => resolve("timeout"), 5000)
      );

      const result = await Promise.race([permissionPromise, timeoutPromise]);

      if (result === "timeout") {
        return false;
      }

      permission = result;

      if (permission === "granted") {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  };

  // Handle FCM token generation for new registration
  const handleFCMTokenGeneration = async (garageId) => {
    try {
      // Check if we should request permission
      let hasPermission = false;

      if (Notification.permission === "granted") {
        hasPermission = true;
      } else if (Notification.permission === "default") {
        hasPermission = await requestNotificationPermission();
      }

      if (hasPermission) {

        // Try to get FCM token
        const token = await requestForToken(garageId, company_id);

        if (token) {
          localStorage.setItem("fcm_token", token);

          // Send token to server immediately
          try {
            const formData = new FormData();
            formData.append("garage_id", garageId);
            formData.append("company_id", company_id);
            formData.append("web_token", token);
            formData.append("platform", "web");

            await fetch(`${server_url}store_token.php`, {
              method: "POST",
              body: formData,
            });

          } catch (serverError) {
            console.error("Failed to store token on server:", serverError);
          }

          return token;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (fcmError) {
      console.error("FCM error during registration:", fcmError);
      return null;
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();

    if (!validateVerifyForm()) {
      return;
    }

    setLoading(true);
    setApiError("");
    setApiSuccess("");

    try {
      const payload = {
        type: "2",
        grage_number: mobileVerified,
        otp: formData.otp,
      };

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        formDataToSend.append(key, payload[key]);
      }

      const response = await axios.post(
        `${server_url}signup.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status == "0") {
        setApiError(response.data.error);
        setLoading(false);
      } else if (response.data.status == "1") {
        const dealerInfo = response.data.result[0];

        if (dealerInfo) {
          localStorage.setItem("userData", JSON.stringify(dealerInfo));
          localStorage.setItem("authToken", "authenticated");

          // Handle FCM token generation in background
          handleFCMTokenGeneration(dealerInfo.grage_id).then((token) => {
            if (token) {
            }
          });

          // Get company details
          try {
            const companyForm = new FormData();
            companyForm.append("company_id", company_id);
            companyForm.append("garage_id", dealerInfo.grage_id);
            companyForm.append("version_code", "1.0");

            // Include web token if we have it
            const storedToken = localStorage.getItem("fcm_token");
            if (storedToken) {
              companyForm.append("web_token", storedToken);
            }

            const userResponse = await axios.post(
              `${server_url}check_user.php`,
              companyForm,
              {
                headers: { "Content-Type": "multipart/form-data" },
              }
            );
            const res = userResponse.data;

            if (res.status !== "1") {
              setLoading(false);
              return;
            }

            const user = res.result[0];
            const company = res.company_details[0];

            // Save user details
            localStorage.setItem("membership_hide_show", res.membership_hide_show);
            localStorage.setItem("grage_upi_id", user.grage_upi_id);
            localStorage.setItem("grage_referal_code", user.grage_referal_code);
            localStorage.setItem("grage_id", user.grage_id);
            localStorage.setItem("garageLoginNumber", user.grage_number);
            localStorage.setItem("grage_number", user.grage_number);
            localStorage.setItem("grage_name", user.grage_name);
            localStorage.setItem("grage_email", user.grage_email);
            localStorage.setItem("grage_address", user.grage_address);
            localStorage.setItem(
              "grage_contact_person_name",
              user.grage_contact_person_name
            );
            localStorage.setItem("grage_city_id", user.grage_city_id);
            localStorage.setItem("grage_state_id", user.grage_state_id);
            localStorage.setItem("grage_gst_no", user.grage_gst_no);
            localStorage.setItem(
              "grage_refered_by",
              user.grage_refered_by || ""
            );

            // Save company details
            localStorage.setItem(
              "company_store_name",
              company.company_store_name
            );
            localStorage.setItem("company_favicon", company.company_favicon);
            localStorage.setItem(
              "company_footer_logo",
              company.company_footer_logo
            );
            localStorage.setItem(
              "company_header_logo",
              company.company_header_logo
            );
            localStorage.setItem("company_email", company.company_email);
            localStorage.setItem("company_mobile", company.company_mobile);
            localStorage.setItem("company_landline", company.company_landline);
            localStorage.setItem("company_address", company.company_address);
            localStorage.setItem(
              "company_terms_and_condition",
              company.company_terms_and_condition
            );
            localStorage.setItem(
              "company_privacy_policy",
              company.company_privacy_policy
            );
            localStorage.setItem("company_about_us", company.company_about_us);
            localStorage.setItem("company_website", company.company_website);

            toast.success("Registration successful! Redirecting...");

            setTimeout(() => {
              navigate("/Login", { replace: true });
            }, 5500);
          } catch (err) {
            console.error("check_user error:", err);
            setLoading(false);
          }
        } else {
          setApiError("User data not found in response");
          setLoading(false);
        }
      } else {
        setApiError(response.data.error || "Verification failed");
        setLoading(false);
      }
    } catch (error) {
      console.error("Verify error:", error);
      if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      } else {
        setApiError("Unable to connect to server. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleBackToSignup = () => {
    setMode("signup");
    setErrors({});
    setApiError("");
    setApiSuccess("");
    setTimer(0);
  };

  const MessageDisplay = () => {
    if (!apiError && !apiSuccess) return null;

    return (
      <div
        style={{
          padding: "12px 16px",
          margin: "16px 0",
          borderRadius: "8px",
          textAlign: "center",
          fontWeight: "500",
          border: apiError ? "1px solid #f56565" : "1px solid #48bb78",
          backgroundColor: apiError ? "#fed7d7" : "#c6f6d5",
          color: apiError ? "#c53030" : "#2d7745",
        }}
      >
        <i
          className={`fas ${
            apiError ? "fa-exclamation-triangle" : "fa-check-circle"
          }`}
          style={{ marginRight: "8px" }}
        ></i>
        {apiError || apiSuccess}
      </div>
    );
  };

  const renderSignupForm = () => (
    <>
      <div className="login-logo">
        <h2>Create Account</h2>
        <p>Join us today</p>
      </div>

      <form className="login-form" onSubmit={handleSignupSubmit}>

        <div className="form-group">
          <label className="form-label">Garage Name *</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-building"></i>
            </div>
            <input
              type="text"
              name="grage_name"
              className={`form-input ${errors.grage_name ? "error" : ""}`}
              placeholder="Garage Name *"
              value={formData.grage_name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          {errors.grage_name && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_name}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Contact Person Name</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-user"></i>
            </div>
            <input
              type="text"
              name="grage_contact_person_name"
              className={`form-input ${
                errors.grage_contact_person_name ? "error" : ""
              }`}
              placeholder="Contact Person Name"
              value={formData.grage_contact_person_name}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          {errors.grage_contact_person_name && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_contact_person_name}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Mobile Number *</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-mobile-alt"></i>
            </div>
            <input
              type="tel"
              name="grage_number"
              className={`form-input ${errors.grage_number ? "error" : ""}`}
              placeholder="10-digit mobile number *"
              value={formData.grage_number}
              onChange={handleChange}
              required
              disabled={loading}
              maxLength="10"
            />
          </div>
          {errors.grage_number && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_number}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Email</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-envelope"></i>
            </div>
            <input
              type="email"
              name="grage_email"
              className={`form-input ${errors.grage_email ? "error" : ""}`}
              placeholder="Email"
              value={formData.grage_email}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          {errors.grage_email && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_email}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Select State *</label>
          <SearchableDropdown
            name="grage_state_id"
            options={states}
            value={formData.grage_state_id}
            onChange={handleChange}
            placeholder="Search and Select State *"
            error={errors.grage_state_id}
            loading={stateLoading}
            disabled={loading}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Select City *</label>
          <SearchableDropdown
            name="grage_city_id"
            options={cities}
            value={formData.grage_city_id}
            onChange={handleChange}
            placeholder="Search and Select City *"
            error={errors.grage_city_id}
            loading={cityLoading}
            disabled={!formData.grage_state_id || loading}
            required
          />
          {!formData.grage_state_id && !cityLoading && (
            <div
              style={{
                color: "#718096",
                fontSize: "14px",
                marginTop: "5px",
                fontStyle: "italic",
              }}
            >
              Please select a state first
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Garage Address</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-map-marker-alt"></i>
            </div>
            <input
              type="text"
              name="grage_address"
              className={`form-input ${errors.grage_address ? "error" : ""}`}
              placeholder="Address"
              value={formData.grage_address}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          {errors.grage_address && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_address}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">GST No</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-file-invoice"></i>
            </div>
            <input
              type="text"
              name="grage_gst_no"
              className={`form-input ${errors.grage_gst_no ? "error" : ""}`}
              placeholder="GST No"
              value={formData.grage_gst_no}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          {errors.grage_gst_no && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_gst_no}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Referral Code (Optional)</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-user-friends"></i>
            </div>
            <input
              type="text"
              name="grage_referral_code"
              className={`form-input ${
                errors.grage_referral_code ? "error" : ""
              }`}
              placeholder="Enter 8-digit referral code"
              value={formData.grage_referral_code}
              onChange={handleChange}
              disabled={loading}
              maxLength="8"
            />
          </div>
          {errors.grage_referral_code && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.grage_referral_code}
            </div>
          )}
          <div
            style={{
              color: "#718096",
              fontSize: "12px",
              marginTop: "5px",
              fontStyle: "italic",
            }}
          ></div>
        </div>
        <MessageDisplay />


        <button
          type="submit"
          className={`login-button ${loading ? "loading" : ""}`}
          disabled={loading || stateLoading}
        >
          {loading && <span className="button-spinner"></span>}
          {loading ? "Creating Account..." : "Create Account"}
        </button>

        <div className="signup-link">
          Already have an account?{" "}
          <Link to="/Login" className="signup-text">
            Sign In
          </Link>
        </div>
      </form>
    </>
  );

  const renderVerifyForm = () => (
    <>
      <div className="login-logo">
        <h2>Verify OTP</h2>
        <p>Enter the 6-digit code sent to {mobileVerified}</p>
      </div>

      <form className="login-form" onSubmit={handleVerifySubmit}>
        <div
          className="mobile-info"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f0f7ff",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            fontSize: "14px",
            color: "#0352a1",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="fas fa-check-circle" style={{ color: "#48bb78" }}></i>
            OTP sent to {mobileVerified}
          </div>
          <button
            type="button"
            onClick={handleBackToSignup}
            disabled={loading}
            style={{
              background: "none",
              border: "1px solid #0352a1",
              color: "#0352a1",
              padding: "4px 12px",
              borderRadius: "4px",
              fontSize: "12px",
              cursor: "pointer",
              transition: "all 0.3s",
            }}
            onMouseOver={(e) => (e.target.style.background = "#0352a1")}
            onMouseOut={(e) => (e.target.style.background = "none")}
          >
            Change
          </button>
        </div>

        <div className="form-group">
          <label className="form-label">OTP</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-key"></i>
            </div>
            <input
              type="tel"
              name="otp"
              className={`form-input ${errors.otp ? "error" : ""}`}
              placeholder="Enter 6-digit OTP"
              value={formData.otp}
              onChange={handleChange}
              maxLength="6"
              disabled={loading}
            />
          </div>
          {errors.otp && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.otp}
            </div>
          )}
        </div>

        <div
          style={{
            textAlign: "center",
            margin: "10px 0 20px",
            fontSize: "14px",
          }}
        >
          {timer > 0 ? (
            <span style={{ color: "#718096" }}>Resend OTP in {timer}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                color: "#0352a1",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "4px",
                transition: "all 0.3s",
              }}
            >
              Resend OTP
            </button>
          )}
        </div>

        <button
          type="submit"
          className={`login-button ${loading ? "loading" : ""}`}
          disabled={loading}
        >
          {loading && <span className="button-spinner"></span>}
          {loading ? "Verifying..." : "Verify OTP"}
        </button>

        <br />
        <MessageDisplay />
      </form>
    </>
  );

  return (
    <div className="login-container">
      {/* Banner Section */}
      <div className="login-banner">
        <img
          src="https://mydigigarage.com/build_v1/img/2.png"
          alt="Garage Banner"
        />
      </div>

      {/* Form Section */}
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {mode == "signup" ? renderSignupForm() : renderVerifyForm()}
        </div>
      </div>
    </div>
  );
};

export default Register;
